import {
  ChangeDetectionStrategy,
  Component,
  effect,
  input,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { form, FormField as SignalFormField } from '@angular/forms/signals';
import {
  SignalColorField,
  SignalDropdown,
  SignalTextarea,
  type TriggerActivatedEvent,
} from '@vallift/ngl';
import {
  BoundTextSlashMenuComponent,
  type SlashMenuAnchorRect,
  type SlashMenuItem,
} from '../../../bound-text-slash-menu/bound-text-slash-menu';
import { formatBoundTextToken } from '../../../../utils/bound-text-template';
import { DEFAULT_TEXT_FONT_FAMILY, TEXT_FONT_FAMILY_OPTIONS } from '../../shared/typography-config.constants';
import { TypographyConfigControlsComponent } from '../../shared/typography-config-controls/typography-config-controls';
import type {
  FormField,
  ParagraphAlignment,
  ParagraphFontFamily,
  ParagraphFontStyle,
  ParagraphFontWeight,
  ParagraphLevel,
} from '../../../../models/built-form';

interface ParagraphConfigModel {
  label: string;
  paragraphLevel: ParagraphLevel;
  paragraphAlignment: ParagraphAlignment;
  paragraphFontStyle: ParagraphFontStyle;
  paragraphFontFamily: ParagraphFontFamily;
  paragraphColor: string;
  paragraphFontWeight: ParagraphFontWeight;
}

interface SelectOption<T> {
  label: string;
  value: T;
}

@Component({
  selector: 'app-paragraph-config-view',
  standalone: true,
  imports: [
    SignalFormField,
    SignalDropdown,
    SignalTextarea,
    SignalColorField,
    TypographyConfigControlsComponent,
    BoundTextSlashMenuComponent,
  ],
  templateUrl: './paragraph-config-view.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ParagraphConfigViewComponent {
  private readonly labelField = viewChild(SignalTextarea);

  readonly field = input.required<FormField>();
  readonly fieldChange = output<FormField>();

  private lastFieldId: string | null = null;
  private suppressEmit = false;
  private pending: TriggerActivatedEvent | null = null;

  readonly paragraphLevelOptions: SelectOption<ParagraphLevel>[] = [
    { label: 'Paragraph 2', value: 2 },
    { label: 'Paragraph 3', value: 3 },
    { label: 'Paragraph 4', value: 4 },
    { label: 'Paragraph 5', value: 5 },
  ];

  readonly fontFamilyOptions = TEXT_FONT_FAMILY_OPTIONS;

  readonly model = signal<ParagraphConfigModel>({
    label: '',
    paragraphLevel: 2,
    paragraphAlignment: 'left',
    paragraphFontStyle: 'normal',
    paragraphFontFamily: DEFAULT_TEXT_FONT_FAMILY,
    paragraphColor: '#0f172a',
    paragraphFontWeight: 400,
  });
  readonly configForm = form(this.model);

  readonly slashMenuOpen = signal(false);
  readonly slashQuery = signal('');
  readonly slashAnchor = signal<SlashMenuAnchorRect | null>(null);

  constructor() {
    effect(() => {
      const next = this.field();
      if (next.id === this.lastFieldId) {
        return;
      }
      this.lastFieldId = next.id;
      this.suppressEmit = true;
      this.closeSlashMenu();
      this.model.set({
        label: next.label,
        paragraphLevel: next.paragraphLevel ?? 2,
        paragraphAlignment: next.paragraphAlignment ?? 'left',
        paragraphFontStyle: next.paragraphFontStyle ?? 'normal',
        paragraphFontFamily: next.paragraphFontFamily ?? DEFAULT_TEXT_FONT_FAMILY,
        paragraphColor: next.paragraphColor ?? '#000000',
        paragraphFontWeight: next.paragraphFontWeight ?? 400,
      });
    });

    effect(() => {
      const draft = this.model();
      if (this.suppressEmit) {
        this.suppressEmit = false;
        return;
      }
      const field = untracked(() => this.field());
      if (!field || field.id !== this.lastFieldId) {
        return;
      }
      this.fieldChange.emit({
        ...field,
        label: draft.label,
        paragraphLevel: draft.paragraphLevel,
        paragraphAlignment: draft.paragraphAlignment,
        paragraphFontStyle: draft.paragraphFontStyle,
        paragraphFontFamily: draft.paragraphFontFamily,
        paragraphColor: draft.paragraphColor,
        paragraphFontWeight: draft.paragraphFontWeight,
        placeholder: undefined,
        helpText: undefined,
        required: undefined,
        validation: undefined,
        options: undefined,
        spacerSize: undefined,
      });
    });
  }

  setLabel(label: string): void {
    this.model.update((model) => ({ ...model, label }));
    this.syncSlashMenuFromValue(label);
  }

  onTriggerActivated(event: TriggerActivatedEvent): void {
    this.pending = event;
    this.slashQuery.set(event.query ?? '');
    this.slashAnchor.set(this.rectFromTarget(this.visibleAnchor(event.target)));
    this.slashMenuOpen.set(true);
  }

  onSlashSelect(item: SlashMenuItem): void {
    if (!this.pending) {
      this.closeSlashMenu();
      return;
    }
    const event = this.pending;
    const token = formatBoundTextToken(item.namespace, item.key, item.title);
    const triggerLen = event.trigger.length + (event.query?.length ?? 0);
    const start = Math.max(0, event.caretIndex - triggerLen);
    const current = this.model().label;
    const next =
      current.slice(0, start) + token + current.slice(event.caretIndex);
    const caret = start + token.length;
    this.closeSlashMenu();
    this.setLabel(next);
    queueMicrotask(() => this.labelField()?.focusHandlebars(caret));
  }

  onSlashDismiss(): void {
    this.closeSlashMenu();
  }

  setFontWeight(weight: ParagraphFontWeight): void {
    this.model.update((model) => ({
      ...model,
      paragraphFontWeight: weight,
    }));
  }

  setAlignment(value: ParagraphAlignment): void {
    this.model.update((current) => ({ ...current, paragraphAlignment: value }));
  }

  setFontStyle(value: ParagraphFontStyle): void {
    this.model.update((current) => ({ ...current, paragraphFontStyle: value }));
  }

  private syncSlashMenuFromValue(label: string): void {
    if (!this.slashMenuOpen() || !this.pending) {
      return;
    }
    const trigger = this.pending.trigger || '/';
    const slashIndex = label.lastIndexOf(trigger);
    if (slashIndex < 0) {
      this.closeSlashMenu();
      return;
    }
    const query = label.slice(slashIndex + trigger.length);
    if (/[\n\r{]/.test(query)) {
      this.closeSlashMenu();
      return;
    }
    this.pending = {
      ...this.pending,
      query,
      caretIndex: slashIndex + trigger.length + query.length,
    };
    this.slashQuery.set(query);
  }

  private closeSlashMenu(): void {
    this.slashMenuOpen.set(false);
    this.slashQuery.set('');
    this.slashAnchor.set(null);
    this.pending = null;
  }

  private visibleAnchor(
    target: HTMLInputElement | HTMLTextAreaElement | HTMLElement,
  ): HTMLElement {
    const wrapped = (target as HTMLElement).closest?.(
      '[data-bound-text-slash-anchor]',
    );
    const visible = wrapped?.querySelector<HTMLElement>(
      '[contenteditable="true"], textarea:not([aria-hidden="true"]), input:not([aria-hidden="true"])',
    );
    return visible ?? (target as HTMLElement);
  }

  private rectFromTarget(target: HTMLElement): SlashMenuAnchorRect {
    const rect = target.getBoundingClientRect();
    return {
      top: rect.top,
      left: rect.left,
      right: rect.right,
      bottom: rect.bottom,
      width: rect.width,
      height: rect.height,
    };
  }
}
